import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { UserService } from 'src/app/services/user/user.service';
import { DirectDebitService } from 'src/app/services/direct-debit/direct-debit.service';
import { ConciliationProgressMonitorComponent } from '../conciliation-progress-monitor/conciliation-progress-monitor.component';
import { saveAs } from 'file-saver';
import * as alertifyjs from 'alertifyjs';
import Swal from 'sweetalert2';
import Chart from 'chart.js/auto';

@Component({
  selector: 'app-domiciliation-config-dashboard',
  templateUrl: './domiciliation-config-dashboard.component.html',
  styleUrls: ['./domiciliation-config-dashboard.component.css']
})
export class DomiciliationConfigDashboardComponent implements OnInit {
  
  // Data
  products: any[] = [];
  filteredProducts: any[] = [];
  banksList: any[] = [];
  domiciliationTypes: any[] = [
    { id: 1, name: 'Cuotas - Diario' },
    { id: 2, name: 'Cuotas - Masivo' },
    { id: 3, name: 'Cuotas - Morosos' },
    { id: 4, name: 'Penalidades' }
  ];
  
  // Filters
  searchTerm = '';
  filterBank = '';
  filterType = '';
  
  // UI State
  isLoading = false;
  selectedProducts: Set<string> = new Set();
  editingProduct: string | null = null;
  activeTab: 'active-debits' | 'product-config' | 'historical-debits' | 'deactivation-log' | 'cobros-reader' | 'alerts-config' | 'creditotal' = 'active-debits';
  
  // Active debits
  activeDirectDebits: any[] = [];
  filteredActiveDebits: any[] = [];

  // Historical debits
  historicalDirectDebits: any[] = [];
  filteredHistoricalDebits: any[] = [];
  groupedHistoricalDebits: Map<string, any[]> = new Map();
  historicalFilterBank = '';
  historicalFilterType = '';
  historicalDate = '';
  // Búsqueda por número de lote. Cuando se usa, el backend ignora la fecha: el sentido de
  // buscar un lote es llegar a él sin saber cuándo fue.
  historicalFilterLote = '';
  isLoadingHistorical = false;
  // Paginado del histórico. Sin esto, limpiar la fecha traía los 4.467 lotes de una vez y
  // la pantalla quedaba colgada.
  historicalPage = 1;
  historicalLimit = 20;
  historicalTotal = 0;
  historicalPages = 1;

  // Grouped by product
  groupedByProduct: Map<string, any[]> = new Map();

  // Marca de la última carga de cada tabla. No hay auto-refresh: el dato es una foto, y
  // sin decir de cuándo es, una foto vieja es peor que no tenerla.
  activeLoadedAt: Date | null = null;
  historicalLoadedAt: Date | null = null;

  // Estados en los que el lote YA pasó por conciliación. Sólo en estos tiene sentido
  // exigir el 100%: un lote UPLOADED al 0% es lo normal, no una anomalía.
  private readonly ESTADOS_CONCILIADOS = ['PROCESING', 'FINISHED'];
  
  // View detail modal
  showDetailModal = false;
  selectedProductDetail: any = null;

  // Timeline modal
  showTimelineModal = false;
  selectedDirectDebit: any = null;
  timelineData: any[] = [];
  /** Cambios manuales sobre el lote (numero del banco, tasa), con autor y hora. */
  timelineCambios: any[] = [];
  /** Archivos de respuesta del banco cargados, uno por intento de conciliacion. */
  timelineArchivos: any[] = [];
  loadingTimeline = false;
  // Ejecuciones reales del lote (trace_documents): duración de cada proceso, avance y
  // los intentos que se cayeron. Antes no se pedían, así que el timeline mostraba sólo
  // instantes y un lote con 3 intentos de conciliación se veía igual que uno limpio.
  timelineRuns: any[] = [];
  timelineSummary: any = null;
  // Eventos de estado + ejecuciones, entrelazados y ordenados por fecha.
  timelineMerged: any[] = [];
  // Los intentos fallidos se agrupan y arrancan colapsados: presentes, sin robar la
  // atención cuando todo salió bien.
  failedAttemptsCollapsed = true;

  // Payments modal
  showPaymentsModal = false;
  paymentsData: any[] = [];
  paymentsCounts = { pending: 0, error: 0, completed: 0, total: 0 };
  paymentsPage = 1;
  paymentsLimit = 100;
  paymentsHasMore = false;
  loadingPayments = false;
  loadingMorePayments = false;
  paymentsSearch = '';
  // Filtro por estado, activado al clickear las tarjetas del resumen.
  // '' = todos, que es el estado por defecto.
  paymentsStatusFilter: '' | 'pending' | 'error' | 'completed' = '';
  activePaymentTab: 'resumen' | 'detalle' | 'estadisticas' = 'resumen'; // Pestaña activa del modal de pagos
  progressMonitorKey = 0; // Key para forzar recreación del componente
  
  // Referencia al componente de progreso
  @ViewChild(ConciliationProgressMonitorComponent) progressMonitor?: ConciliationProgressMonitorComponent;

  // Insurance Report modal
  showInsuranceReportModal = false;
  insuranceReportData: any[] = [];
  insuranceReportStats: any = null;
  loadingInsuranceReport = false;
  
  // Deactivation Log Tab
  deactivationBatches: any[] = [];
  deactivationBatchesTotal = 0;
  deactivationBatchesPage = 1;
  deactivationBatchesLimit = 20;
  isLoadingDeactivationBatches = false;

  showDeactivationDetailModal = false;
  selectedDeactivationBatch: any = null;
  selectedDeactivationBatchDetail: any = null;
  isLoadingDeactivationDetail = false;

  affectedPersonsData: any[] = [];
  affectedPersonsTotal = 0;
  affectedPersonsPage = 1;
  affectedPersonsLimit = 20;
  affectedPersonsSearch = '';
  isLoadingMorePersons = false;
  isSearchingPersons = false;

  deactivationSearchIdentity = '';
  deactivationSearchResult: any = null;
  isSearchingDeactivation = false;

  isRunningDeactivation = false;

  // Bulk edit
  showBulkEditModal = false;
  bulkBanksToAdd: string[] = [];
  bulkTypesToAdd: number[] = [];
  bulkAction: 'add' | 'replace' | 'remove' = 'add';

  constructor(
    private _user: UserService,
    private router: Router,
    private directDebitService: DirectDebitService
  ) { }

  async ngOnInit() {
    await this.loadActiveDirectDebits();
  }

  switchTab(tab: 'active-debits' | 'product-config' | 'historical-debits' | 'deactivation-log' | 'cobros-reader' | 'alerts-config' | 'creditotal') {
    this.activeTab = tab;
    if (tab === 'product-config' && this.products.length === 0) {
      this.loadProductsData();
    } else if (tab === 'historical-debits') {
      if (!this.historicalDate) {
        const today = new Date();
        this.historicalDate = today.toISOString().split('T')[0];
      }
      if (this.banksList.length === 0) {
        this.loadBanks();
      }
    } else if (tab === 'deactivation-log' && this.deactivationBatches.length === 0) {
      this.loadDeactivationBatches(1);
    } else if (tab === 'alerts-config' && !this.alertsConfig) {
      this.loadAlertsConfig();
    }
  }

  // ══ Lote manual del banco ══════════════════════════════════════════════════
  // Algunos bancos (Bancaribe y compañía) exigen su propio número de lote, que se
  // teclea a mano al confirmar la carga del archivo. Se carga mal a menudo y hasta
  // ahora no había forma de corregirlo: el lote quedaba con el número equivocado
  // para siempre. Solo se puede editar mientras el proceso siga vivo.

  /** Estados en los que el número ya no debe tocarse: el lote se concilió contra él. */
  private readonly ESTADOS_LOTE_CERRADO = ['FINISHED', 'ABORTED'];

  /**
   * ¿Este lote es de un banco que usa número manual?
   *
   * La señal buena es el flag del banco, pero un backend que aún no lo envíe dejaría la
   * fila muda. Por eso también se considera manual cualquier lote que YA tenga un número
   * asignado: si lo tiene, alguien lo tecleó, que es exactamente el caso a corregir.
   */
  usaLoteManual(dd: any): boolean {
    return !!(dd?.bank?.direct_debit_manual_lote || dd?.manual_lote);
  }

  puedeEditarLoteManual(dd: any): boolean {
    if (!this.usaLoteManual(dd)) return false;
    if (dd.completed === true || dd.active === false) return false;
    return !this.ESTADOS_LOTE_CERRADO.includes(dd.status);
  }

  async editarLoteManual(dd: any) {
    const esCorreccion = !!dd.manual_lote;
    const result = await Swal.fire({
      title: esCorreccion ? 'Corregir número de lote del banco' : 'Asignar número de lote del banco',
      html: `Lote interno: <b>${dd.lote}</b><br>Banco: <b>${dd.bank?.name || '-'}</b>` +
            (esCorreccion ? `<br><br>Número actual: <b>${dd.manual_lote}</b>` : ''),
      input: 'text',
      inputValue: dd.manual_lote || '',
      inputAttributes: { autocapitalize: 'off' },
      inputPlaceholder: 'Número que asignó el banco',
      showCancelButton: true,
      confirmButtonText: 'Guardar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      inputValidator: (value) => (!value || !value.trim()) ? 'El número de lote es obligatorio' : null
    });

    if (!result.isConfirmed) return;

    const nuevo = String(result.value).trim();
    if (nuevo === (dd.manual_lote || '')) return;   // nada que hacer

    try {
      const r = await this.directDebitService.direct_debit_manual_lote({ id: dd._id, manual_lote: nuevo });
      if (r?.success) {
        dd.manual_lote = nuevo;   // refresco optimista de la fila
        Swal.fire({
          icon: 'success',
          title: esCorreccion ? 'Número corregido' : 'Número asignado',
          text: `El lote ${dd.lote} quedó con el número ${nuevo}.`,
          timer: 2200,
          showConfirmButton: false
        });
        await this.loadActiveDirectDebits();
      } else {
        Swal.fire({ icon: 'error', title: 'No se pudo guardar', text: r?.msg || 'El servidor rechazó el cambio' });
      }
    } catch (e: any) {
      // El backend explica el motivo (banco sin lote manual, proceso ya finalizado…).
      // Mostrarlo tal cual evita que el usuario tenga que adivinar.
      Swal.fire({
        icon: 'error',
        title: 'No se pudo guardar',
        text: e?.error?.msg || e?.message || 'Error de comunicación con el servidor'
      });
    }
  }

  // ══ Pestaña "Alertas" ══════════════════════════════════════════════════════
  // Configuración de las alertas de domiciliación en Discord. Vive en base de datos
  // para poder apagarlas o ajustarlas sin redeploy: tocar la config del servidor y
  // reiniciar es justo lo que no se puede hacer en medio de un incidente.

  alertsConfig: any = null;
  alertsAmbiente = '';
  alertsCacheTtlMs = 30000;
  alertsLoading = false;
  alertsSaving = false;
  alertsTesting = false;
  alertsDirty = false;
  alertsMostrarAjustes = false;
  alertsWebhookVisible = false;
  alertsWebhookNuevo = '';   // solo se manda si el usuario escribe uno
  alertsMensaje: { tipo: 'ok' | 'error'; texto: string } | null = null;
  alertsError = '';          // detalle del fallo de carga, para no dejar la pestaña muda

  /**
   * Orden y textos de la tabla de eventos. El orden es por gravedad.
   *
   * `via` distingue quién emite la alerta, que es lo primero que hay que saber cuando una
   * no llega (ver services/domiciliation-alerts.js en el backend):
   *   'caliente'  → la manda el propio proceso de conciliación, al instante. Sólo llega si
   *                 el proceso sigue vivo para contarlo.
   *   'vigilante' → la descubre el barrido periódico (cada 5 min). Existe justamente para
   *                 lo que el proceso no pudo avisar porque murió.
   *
   * `repite` refleja el campo `reminders` del backend: sólo las tres críticas insisten
   * (30 min, 1 h, 2 h, 4 h) y se silencian a las 12 h.
   */
  readonly alertsEventos = [
    {
      key: 'CONCILIACION_FALLIDA', icono: '🔴', nombre: 'Conciliación fallida',
      desc: 'El proceso terminó en error y el lote quedó bloqueado',
      via: 'caliente', repite: true,
      detecta: 'El proceso de conciliación entró en su manejo de errores y cortó sin terminar el lote.',
      implica: 'El lote queda bloqueado: su proceso sigue figurando como activo, así que la consola rechaza cualquier intento nuevo con "ya se está conciliando". Hay que reiniciar el proceso desde el paso 5 para poder reintentar.',
      cuando: 'Al instante — salvo que el proceso haya muerto de golpe, en cuyo caso avisa "Proceso sin avanzar".'
    },
    {
      key: 'PROCESO_HUERFANO', icono: '🔴', nombre: 'Proceso sin avanzar',
      desc: 'Sin señales de vida — probable reinicio del backend',
      via: 'vigilante', repite: true,
      detecta: 'Un proceso quedó marcado como en curso pero dejó de reportar avance por más del umbral configurado (el tiempo estimado × factor, con un piso).',
      implica: 'El proceso murió sin alcanzar a avisar: reinicio del backend, falta de memoria o de CPU. El lote quedó a medias y los cobros ya procesados sí se registraron.',
      cuando: 'Hasta 5 minutos después de superar el umbral, cuando pasa el vigilante.'
    },
    {
      key: 'LOTE_BLOQUEADO', icono: '🔴', nombre: 'Lote bloqueado',
      desc: 'Un proceso muerto impide reintentar el lote',
      via: 'vigilante', repite: true,
      detecta: 'El proceso figura en error o cancelado, pero quedó marcado como activo.',
      implica: 'Nadie puede reconciliar ese lote: el control de entrada sólo mira si hay un proceso activo, no si murió. El operador ve "ya se está conciliando" sin saber que no hay nada corriendo.',
      cuando: 'Hasta 5 minutos después, cuando pasa el vigilante.'
    },
    {
      key: 'COBROS_EN_LIMBO', icono: '🟠', nombre: 'Cobros sin resolver',
      desc: 'Quedaron cobros en PENDIENTE al finalizar',
      via: 'caliente', repite: false,
      detecta: 'El lote cerró pero algunos cobros nunca salieron de PENDIENTE.',
      implica: 'De esos cobros no se sabe si el banco los debitó o no. Hay que contrastarlos contra la respuesta del banco antes de darlos por perdidos: si el banco cobró y el sistema no lo registró, el cliente pagó sin que se le acredite.',
      cuando: 'Al cerrar el lote. El mensaje lista los primeros 10 con cédula, monto y referencia.'
    },
    {
      key: 'LOTE_SIN_CERRAR', icono: '🟠', nombre: 'Lote sin cerrar',
      desc: 'Se quedó a medio camino sin llegar a FINISHED',
      via: 'vigilante', repite: false,
      detecta: 'El lote avanzó en el flujo y se quedó detenido sin llegar a finalizado, sin moverse durante un buen rato.',
      implica: 'Quedó a mitad del proceso y nadie lo retomó. No es un error del sistema: es trabajo pendiente que alguien tiene que continuar.',
      cuando: 'Hasta 5 minutos después de superar el doble del piso configurado.'
    },
    {
      key: 'COMPLETADO_SIN_CONCILIAR', icono: '🟠', nombre: 'Completado sin conciliar',
      desc: 'Marcado como completo sin haber conciliado',
      via: 'vigilante', repite: false,
      detecta: 'El lote figura como completado pero su estado no llegó a finalizado.',
      implica: 'Se dio por terminado sin haber procesado la respuesta del banco. Los cobros de ese lote no se registraron como pagos, así que las cuotas siguen figurando impagas.',
      cuando: 'Hasta 5 minutos después, cuando pasa el vigilante.'
    },
    {
      key: 'DESCUADRE_CONCILIACION', icono: '🔵', nombre: 'Descuadre de conciliación',
      desc: 'Informativa · nunca se repite',
      via: 'caliente', repite: false,
      detecta: 'Al cerrar quedó dinero cobrado que no se aplicó a ninguna cuota ni fee.',
      implica: 'No siempre es un problema: suele ser un pago que quedó con saldo a favor. Requiere que un analista lo confirme y lo aplique, no una corrección técnica.',
      cuando: 'Al cerrar el lote, si el saldo sin aplicar supera un centavo.'
    },
    {
      key: 'CONCILIACION_OK', icono: '🟢', nombre: 'Conciliación completa',
      desc: 'Resumen del camino feliz · un mensaje por lote',
      via: 'caliente', repite: false,
      detecta: 'El lote cerró sin cobros pendientes y sin saldo sin aplicar.',
      implica: 'Nada que hacer. Sirve como confirmación de que el lote terminó bien, y como señal de que el canal de alertas está vivo.',
      cuando: 'Al cerrar el lote. Es el único mensaje del camino feliz: si está apagado, un lote correcto no genera ningún aviso.'
    }
  ];

  /** Qué fila del catálogo está expandida. Sólo una a la vez, para no llenar la pantalla. */
  alertsEventoAbierto: string | null = null;

  toggleDetalleEvento(key: string) {
    this.alertsEventoAbierto = this.alertsEventoAbierto === key ? null : key;
  }

  async loadAlertsConfig() {
    this.alertsLoading = true;
    this.alertsMensaje = null;
    this.alertsError = '';
    try {
      const r = await this.directDebitService.getAlertsConfig();
      this.alertsConfig = r?.config || null;
      this.alertsAmbiente = r?.ambiente || '';
      this.alertsCacheTtlMs = r?.cache_ttl_ms ?? 30000;
      this.alertsWebhookNuevo = '';
      this.alertsDirty = false;
      if (!this.alertsConfig) this.alertsError = 'El servidor respondió sin configuración.';
    } catch (e: any) {
      this.alertsConfig = null;
      // El detalle importa: distinguir un 404 (backend viejo, sin el endpoint) de un
      // 401 (sesión vencida) ahorra media hora de diagnóstico.
      const status = e?.status ? `HTTP ${e.status}` : 'sin respuesta del servidor';
      this.alertsError = `${status}${e?.error?.msg ? ` — ${e.error.msg}` : ''}`;
      console.error('[alertas] Error cargando configuración:', e);
    } finally {
      this.alertsLoading = false;
    }
  }

  /** ¿Está en producción? Se usa para pintar el aviso en rojo. */
  get alertsEsProduccion(): boolean {
    return /PRODUC/i.test(this.alertsAmbiente || '');
  }

  /**
   * Solo se puede probar si hay algo que probar: un webhook recién escrito, o uno ya
   * guardado. Sin esto el botón parece disponible y falla, que es peor que estar gris.
   */
  get puedeProbarWebhook(): boolean {
    return !!(this.alertsWebhookNuevo?.trim() || this.alertsConfig?.webhook_configurado);
  }

  marcarAlertsDirty() { this.alertsDirty = true; this.alertsMensaje = null; }

  eventoConfig(key: string) {
    if (!this.alertsConfig) return { enabled: false, mention: false };
    if (!this.alertsConfig.events) this.alertsConfig.events = {};
    if (!this.alertsConfig.events[key]) this.alertsConfig.events[key] = { enabled: false, mention: false };
    return this.alertsConfig.events[key];
  }

  toggleEvento(key: string, campo: 'enabled' | 'mention') {
    const ev = this.eventoConfig(key);
    ev[campo] = !ev[campo];
    // No tiene sentido mencionar en un evento apagado.
    if (campo === 'enabled' && !ev.enabled) ev.mention = false;
    this.marcarAlertsDirty();
  }

  async saveAlertsConfig() {
    this.alertsSaving = true;
    this.alertsMensaje = null;
    try {
      const body: any = {
        enabled: this.alertsConfig.enabled,
        events: this.alertsConfig.events,
        stuck_factor: Number(this.alertsConfig.stuck_factor),
        stuck_min_minutes: Number(this.alertsConfig.stuck_min_minutes),
        reminders_enabled: this.alertsConfig.reminders_enabled,
        mute_after_hours: Number(this.alertsConfig.mute_after_hours)
      };
      // El webhook solo viaja si se escribió uno nuevo: así no se pisa el real
      // con el valor enmascarado que se muestra en pantalla.
      if (this.alertsWebhookNuevo?.trim()) body.webhook_url = this.alertsWebhookNuevo.trim();

      await this.directDebitService.saveAlertsConfig(body);
      this.alertsMensaje = {
        tipo: 'ok',
        texto: `Guardado. Los cambios se aplican en toda la flota en menos de ${Math.round(this.alertsCacheTtlMs / 1000)} segundos.`
      };
      this.alertsDirty = false;
      await this.loadAlertsConfig();
    } catch (e: any) {
      const detalle = e?.error?.msg || e?.message || '';
      const status = e?.status ? `HTTP ${e.status}` : 'sin respuesta del servidor';
      this.alertsMensaje = { tipo: 'error', texto: `No se pudo guardar — ${detalle || status}` };
      console.error('[alertas] Error guardando configuración:', e);
    } finally {
      this.alertsSaving = false;
    }
  }

  async testAlertsWebhook() {
    this.alertsTesting = true;
    this.alertsMensaje = null;
    try {
      const r = await this.directDebitService.testAlertsWebhook(this.alertsWebhookNuevo?.trim() || undefined);
      this.alertsMensaje = {
        tipo: r?.success ? 'ok' : 'error',
        texto: r?.msg || (r?.success ? 'Mensaje de prueba enviado' : 'No se pudo enviar')
      };
      if (r?.success) await this.loadAlertsConfig();
    } catch (e: any) {
      // El motivo real viene del backend ("No hay webhook configurado para probar",
      // "Discord respondió 401"…). Reemplazarlo por un genérico obliga a adivinar.
      const detalle = e?.error?.msg || e?.message || '';
      const status = e?.status ? `HTTP ${e.status}` : 'sin respuesta del servidor';
      this.alertsMensaje = {
        tipo: 'error',
        texto: `No se pudo enviar la prueba — ${detalle || status}`
      };
      console.error('[alertas] Error probando webhook:', e);
    } finally {
      this.alertsTesting = false;
    }
  }

  async loadBanks() {
    try {
      const banksResp = await this._user.getBanksWithDirectDebitEnabled({});
      this.banksList = banksResp?.res || banksResp?.resp || [];
    } catch (error) {
      console.error('Error loading banks:', error);
    }
  }

  async loadActiveDirectDebits() {
    this.isLoading = true;
    try {
      // Load banks if not loaded
      if (this.banksList.length === 0) {
        const banksResp = await this._user.getBanksWithDirectDebitEnabled({});
        this.banksList = banksResp?.res || banksResp?.resp || [];
      }

      // Get active direct debits from API
      const response = await this._user.getActiveDirectDebits();
      this.activeDirectDebits = response?.resp || response?.res || [];
      this.activeLoadedAt = new Date();

      // Apply filters
      this.applyActiveDebitsFilters();
    } catch (error) {
      console.error('Error loading active direct debits:', error);
      alertifyjs.error('Error al cargar domiciliaciones activas');
    } finally {
      this.isLoading = false;
    }
  }

  applyActiveDebitsFilters() {
    let filtered = [...this.activeDirectDebits];

    // Filter by bank
    if (this.filterBank) {
      filtered = filtered.filter(dd => dd.bank?.code === this.filterBank);
    }

    // Filter by type
    if (this.filterType) {
      filtered = filtered.filter(dd => dd.type === this.filterType);
    }

    this.filteredActiveDebits = filtered;

    // Group by product
    this.groupedByProduct = new Map();
    this.filteredActiveDebits.forEach((dd: any) => {
      const productCode = dd.params?.product_type || 'CREDIT';
      if (!this.groupedByProduct.has(productCode)) {
        this.groupedByProduct.set(productCode, []);
      }
      this.groupedByProduct.get(productCode)?.push(dd);
    });
  }

  // ── Indicador de completitud ───────────────────────────────────────────────
  // Un lote puede quedar en FINISHED con registros que nunca se resolvieron (hoy hay 183
  // así). La señal es el % de detalles que salieron de PENDING; el backend lo devuelve en
  // processed_stats. Acá sólo se decide cómo mostrarlo.

  /** ¿El lote ya pasó por conciliación? Antes de eso, estar al 0% es lo esperado. */
  esConciliado(dd: any): boolean {
    return this.ESTADOS_CONCILIADOS.indexOf(dd?.status) !== -1;
  }

  /** ¿Hay algo que mostrar? Sin stats (lote fuera del tope, o sin detalles) no se dibuja. */
  tieneCompletitud(dd: any): boolean {
    return !!dd?.processed_stats && dd.processed_stats.total > 0;
  }

  /**
   * Porcentaje ya calculado por el backend, sin redondear.
   * Un lote con 2 pendientes de 707 vale 99.72: redondearlo lo mostraría como 100% y
   * escondería justo el caso que este indicador existe para delatar.
   */
  porcentajeProcesado(dd: any): number {
    return this.tieneCompletitud(dd) ? (dd.processed_stats.percentage || 0) : 0;
  }

  /** Texto del porcentaje: entero cuando es exacto, con decimales cuando no lo es. */
  porcentajeTexto(dd: any): string {
    if (!this.tieneCompletitud(dd)) return '—';
    const p = this.porcentajeProcesado(dd);
    return (p === 100 || p === 0) ? `${p}%` : `${p.toFixed(2)}%`;
  }

  /**
   * La alerta. Sólo se enciende en lotes ya conciliados a los que les falta algo:
   * marcar en rojo todo lo que no esté al 100% llenaría la tabla de ruido y volvería
   * invisible al indicador.
   */
  tieneAlerta(dd: any): boolean {
    return this.esConciliado(dd) && this.tieneCompletitud(dd) && dd.processed_stats.pending > 0;
  }

  /** Detalle accionable para el tooltip: el número crudo, no el porcentaje. */
  completitudTooltip(dd: any): string {
    if (!this.tieneCompletitud(dd)) return 'Sin registros cargados para este lote';
    const s = dd.processed_stats;
    if (s.pending === 0) return `Los ${s.total} registros fueron procesados`;
    const base = `${s.pending} de ${s.total} registros sin procesar`;
    return this.esConciliado(dd) ? base : `${base} · el lote aún no se concilió`;
  }

  /** "hace N min" para la marca de última carga. */
  desdeHace(fecha: Date | null): string {
    if (!fecha) return '';
    const seg = Math.floor((Date.now() - fecha.getTime()) / 1000);
    if (seg < 60) return 'recién';
    const min = Math.floor(seg / 60);
    if (min < 60) return `hace ${min} min`;
    const h = Math.floor(min / 60);
    return h < 24 ? `hace ${h} h` : `hace ${Math.floor(h / 24)} d`;
  }

  clearActiveDebitsFilters() {
    this.filterBank = '';
    this.filterType = '';
    this.applyActiveDebitsFilters();
  }

  // Historical Debits Methods
  async loadHistoricalDirectDebits() {
    this.isLoadingHistorical = true;
    try {
      // Get historical direct debits from backend
      const response = await this._user.getHistoricalDirectDebits(
        this.historicalDate,
        this.historicalFilterBank,
        this.historicalFilterType,
        this.historicalFilterLote,
        this.historicalPage,
        this.historicalLimit
      );

      this.historicalDirectDebits = response?.resp || [];
      const pag = response?.pagination;
      this.historicalTotal = pag?.total ?? this.historicalDirectDebits.length;
      this.historicalPages = pag?.pages ?? 1;
      this.historicalPage = pag?.page ?? this.historicalPage;
      this.historicalLoadedAt = new Date();
      this.filteredHistoricalDebits = this.historicalDirectDebits;
      this.updateGroupedHistoricalDebits();
    } catch (error) {
      console.error('Error loading historical debits:', error);
      if (typeof alertifyjs !== 'undefined') {
        alertifyjs.error('Error al cargar histórico de domiciliaciones');
      }
    } finally {
      this.isLoadingHistorical = false;
    }
  }

  async applyHistoricalFilters() {
    // Cualquier cambio de filtro reinicia el paginado: seguir en la página 7 de un
    // resultado distinto muestra una página vacía y parece que no hay nada.
    this.historicalPage = 1;
    await this.loadHistoricalDirectDebits();
  }

  /** Cambia de página del histórico, acotando a los límites reales. */
  async irAPaginaHistorico(pagina: number) {
    const destino = Math.min(Math.max(1, pagina), this.historicalPages);
    if (destino === this.historicalPage) return;
    this.historicalPage = destino;
    await this.loadHistoricalDirectDebits();
  }

  /** Rango que se está viendo, para el texto "1–20 de 4.467". */
  get rangoHistorico(): { desde: number; hasta: number } {
    if (this.historicalTotal === 0) return { desde: 0, hasta: 0 };
    const desde = (this.historicalPage - 1) * this.historicalLimit + 1;
    return { desde, hasta: Math.min(desde + this.historicalDirectDebits.length - 1, this.historicalTotal) };
  }

  clearHistoricalFilters() {
    this.historicalFilterBank = '';
    this.historicalFilterType = '';
    this.historicalDate = '';
    this.historicalFilterLote = '';
    this.applyHistoricalFilters();
  }

  /**
   * Buscar por lote no puede convivir con el filtro de fecha, que arranca en hoy: se
   * limpia al tipear un lote, si no la búsqueda no devolvería nada y parecería que el
   * lote no existe. El backend además ignora la fecha cuando recibe un lote.
   */
  buscarPorLote() {
    if (this.historicalFilterLote?.trim()) {
      this.historicalDate = '';
    }
    this.applyHistoricalFilters();
  }

  updateGroupedHistoricalDebits() {
    const grouped = new Map<string, any[]>();

    this.filteredHistoricalDebits.forEach(dd => {
      const productCode = dd.params?.product_type || 'CREDIT';
      if (!grouped.has(productCode)) {
        grouped.set(productCode, []);
      }
      grouped.get(productCode)!.push(dd);
    });

    this.groupedHistoricalDebits = grouped;
  }

  exportHistoricalDebitsToCSV() {
    try {
      const headers = [
        'Producto',
        'Código Producto',
        'Lote',
        'Tipo',
        'Banco',
        'Código Banco',
        'Fecha',
        'Estado',
        'Monto Total',
        'Tasa'
      ];

      const rows = this.filteredHistoricalDebits.map((dd: any) => {
        const loteValue = dd.lote || dd.manual_lote || '';
        return [
          dd.product?.name || 'Sin Producto',
          dd.product?.code || '',
          `="${loteValue}"`, // Force string format in Excel
          dd.type_name || '',
          dd.bank?.name || '',
          dd.bank?.code || '',
          // Misma hora que muestra la tabla: el arranque real del proceso.
          this.formatDate(this.fechaInicioProceso(dd)),
          dd.status || 'COMPLETADO',
          dd.total_amount?.toFixed(2) || '0.00',
          dd.rate?.toFixed(2) || '0.00'
        ];
      });

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      const date = new Date().toISOString().split('T')[0];
      saveAs(blob, `historico_domiciliaciones_${date}.csv`);

      if (typeof alertifyjs !== 'undefined') {
        alertifyjs.success('CSV exportado exitosamente');
      }
    } catch (error) {
      console.error('Error exporting CSV:', error);
      if (typeof alertifyjs !== 'undefined') {
        alertifyjs.error('Error al exportar CSV');
      }
    }
  }

  exportActiveDebitsToCSV() {
    if (this.filteredActiveDebits.length === 0) {
      if (typeof alertifyjs !== 'undefined') {
        alertifyjs.warning('No hay domiciliaciones para exportar');
      }
      return;
    }

    // Preparar datos para CSV
    const csvData: any[] = [];
    
    // Agrupar por producto
    this.groupedByProduct.forEach((debits, productCode) => {
      const productName = this.getProductName(productCode);
      
      debits.forEach((dd: any) => {
        csvData.push({
          'Producto': productName,
          'Código Producto': productCode,
          'Lote': `="${dd.lote || dd.manual_lote || '-'}"`, // Forzar como string en Excel
          'Tipo': dd.type_name || this.getTypeName(dd.type),
          'Banco': dd.bank?.name || '-',
          'Código Banco': dd.bank?.code || '-',
          // Misma hora que muestra la tabla: el arranque real del proceso.
          'Fecha': this.formatDate(this.fechaInicioProceso(dd)),
          'Estado': dd.completed ? 'COMPLETADO' : dd.status || 'ACTIVO',
          'Monto Total': dd.total_amount || 0,
          'Tasa': dd.rate || 0
        });
      });
    });

    // Convertir a CSV
    const headers = Object.keys(csvData[0]);
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => 
        headers.map(header => {
          const value = row[header];
          // Escapar valores que contengan comas o comillas
          if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        }).join(',')
      )
    ].join('\n');

    // Descargar archivo
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const date = new Date().toISOString().split('T')[0];
    saveAs(blob, `domiciliaciones_activas_${date}.csv`);

    if (typeof alertifyjs !== 'undefined') {
      alertifyjs.success('CSV exportado exitosamente');
    }
  }

  async loadProductsData() {
    this.isLoading = true;
    try {
      // Load all products
      const productsResp = await this._user.getActiveProductsInvoices(true);
      const allProducts = productsResp?.resp || productsResp?.res || [];
      
      // Load banks
      const banksResp = await this._user.getBanksWithDirectDebitEnabled({});
      this.banksList = banksResp?.res || banksResp?.resp || [];
      
      // Load domiciliation config for each product
      this.products = await Promise.all(
        allProducts.map(async (product: any) => {
          try {
            const config = await this._user.getRemanenteNeteo(product.params.code);
            return {
              code: product.params.code,
              name: product.params.name,
              status: product.params.status,
              domiciliacion: config?.domiciliacion || { status: false, allowed_banks: [], allowed_types: [] }
            };
          } catch (error) {
            return {
              code: product.params.code,
              name: product.params.name,
              status: product.params.status,
              domiciliacion: { status: false, allowed_banks: [], allowed_types: [] }
            };
          }
        })
      );
      
      this.applyFilters();
    } catch (error) {
      console.error('Error loading data:', error);
      alertifyjs.error('Error al cargar los datos');
    } finally {
      this.isLoading = false;
    }
  }

  applyFilters() {
    let filtered = [...this.products];
    
    // Search filter
    if (this.searchTerm) {
      const term = this.searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        p.code.toLowerCase().includes(term) || 
        p.name.toLowerCase().includes(term)
      );
    }
    
    this.filteredProducts = filtered;
  }

  getProductName(productCode: string): string {
    const product = this.products.find(p => p.code === productCode);
    return product?.name || productCode;
  }

  getTypeName(typeId: number): string {
    const type = this.domiciliationTypes.find(t => t.id === typeId);
    return type?.name || String(typeId);
  }

  formatDate(date: string | Date | null | undefined): string {
    if (!date) return '-';
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Hora real en la que arrancó el proceso del lote.
   *
   * `date` es la fecha de negocio del lote y se guarda a medianoche, así que la columna
   * mostraba siempre "00:00": un dato inútil justo cuando se quiere saber a qué hora
   * corrió el proceso. El instante de arranque no viene en un campo propio —la colección
   * direct_debits no lleva created_at— pero sí está embebido en el _id: los primeros
   * 4 bytes de un ObjectId son la marca de creación en segundos. De ahí se lee, sin
   * depender de un cambio en el backend.
   *
   * Si el _id no tuviera forma de ObjectId, se cae de vuelta a `date` antes que dejar
   * la celda vacía.
   */
  fechaInicioProceso(dd: any): Date | null {
    const id = String(dd?._id || '');
    if (/^[0-9a-f]{24}$/i.test(id)) {
      return new Date(parseInt(id.substring(0, 8), 16) * 1000);
    }
    return dd?.date ? new Date(dd.date) : null;
  }

  /**
   * Aclara al pasar el mouse qué es cada fecha. Importa cuando el proceso arranca de
   * madrugada: ahí el día del arranque y la fecha de negocio del lote no coinciden.
   */
  fechaInicioTooltip(dd: any): string {
    const inicio = this.fechaInicioProceso(dd);
    const negocio = dd?.date
      ? new Date(dd.date).toLocaleDateString('es-ES', { year: 'numeric', month: '2-digit', day: '2-digit' })
      : '-';
    return `Arranque del proceso: ${inicio ? this.formatDate(inicio) : '-'}\nFecha del lote: ${negocio}`;
  }

  // Selection
  toggleSelection(productCode: string) {
    if (this.selectedProducts.has(productCode)) {
      this.selectedProducts.delete(productCode);
    } else {
      this.selectedProducts.add(productCode);
    }
  }

  selectAll() {
    this.filteredProducts.forEach(p => this.selectedProducts.add(p.code));
  }

  clearSelection() {
    this.selectedProducts.clear();
  }

  // Bank/Type helpers
  getBankName(code: string): string {
    const bank = this.banksList.find(b => b.code === code);
    return bank?.name || code;
  }

  bankNameByCode(code: string): string {
    const b = (this.banksList || []).find((x: any) => x.code === code);
    return b?.name || code;
  }

  typeNameById(id: number): string {
    const t = (this.domiciliationTypes || []).find((x: any) => x.id === id);
    return t?.name || String(id);
  }

  getBanksSummary(product: any): string {
    if (!product.domiciliacion.status) return '-';
    if (!product.domiciliacion.allowed_banks || product.domiciliacion.allowed_banks.length === 0) {
      return 'Todos';
    }
    return `${product.domiciliacion.allowed_banks.length} seleccionados`;
  }

  getTypesSummary(product: any): string {
    if (!product.domiciliacion.status) return '-';
    if (!product.domiciliacion.allowed_types || product.domiciliacion.allowed_types.length === 0) {
      return 'Todos';
    }
    return `${product.domiciliacion.allowed_types.length} seleccionados`;
  }

  getBanksTooltip(product: any): string {
    if (!product.domiciliacion.allowed_banks || product.domiciliacion.allowed_banks.length === 0) {
      return 'Todos los bancos';
    }
    return product.domiciliacion.allowed_banks.map((code: string) => this.getBankName(code)).join(', ');
  }

  getTypesTooltip(product: any): string {
    if (!product.domiciliacion.allowed_types || product.domiciliacion.allowed_types.length === 0) {
      return 'Todos los tipos';
    }
    return product.domiciliacion.allowed_types.map((id: number) => this.getTypeName(id)).join(', ');
  }

  // Navigation
  viewProductDetail(product: any) {
    this.selectedProductDetail = product;
    this.showDetailModal = true;
  }

  closeDetailModal() {
    this.showDetailModal = false;
    this.selectedProductDetail = null;
  }

  async openTimelineModal(directDebit: any) {
    console.log('Opening timeline modal for:', directDebit);
    this.selectedDirectDebit = directDebit;
    this.showTimelineModal = true;
    this.loadingTimeline = true;
    this.timelineData = [];
    this.timelineCambios = [];
    this.timelineArchivos = [];

    try {
      const response = await this._user.getDirectDebitTimeline(directDebit._id);
      const data = response?.resp || {};
      this.timelineData = data.timeline || [];
      this.timelineRuns = data.runs || [];
      this.timelineSummary = data.summary || null;
      // Traza de cambios manuales sobre el lote (número del banco, tasa). Se muestra
      // en el paso "Cargado al banco", que es donde el número del banco importa.
      // Más reciente primero: al revisar un problema interesa el último cambio.
      this.timelineArchivos = (data.direct_debit?.response_files || [])
        .slice()
        .sort((a: any, b: any) => new Date(b.at).getTime() - new Date(a.at).getTime());
      this.timelineCambios = (data.direct_debit?.change_history || [])
        .slice()
        .sort((a: any, b: any) => new Date(b.at).getTime() - new Date(a.at).getTime());
      this.timelineMerged = this.buildMergedTimeline(this.timelineData, this.timelineRuns);
    } catch (error) {
      console.error('Error loading timeline:', error);
      if (typeof alertifyjs !== 'undefined') {
        alertifyjs.error('Error al cargar el timeline');
      }
    } finally {
      this.loadingTimeline = false;
    }
  }

  /**
   * Entrelaza los cambios de estado del lote con las ejecuciones reales, ordenados por
   * fecha, para que la línea de tiempo cuente lo que de verdad pasó y no sólo los hitos.
   * Las conciliaciones fallidas salen del hilo principal: se agrupan aparte para no
   * enterrar el intento que sí funcionó.
   */
  buildMergedTimeline(events: any[], runs: any[]): any[] {
    const items: any[] = (events || []).map(e => ({
      kind: 'EVENT', date: e.date, event: e
    }));

    (runs || []).forEach(r => {
      const esFallida = r.kind === 'CONCILIATION' && r.state !== 'COMPLETE';
      if (esFallida) return; // van agrupados, ver failedRuns()
      items.push({ kind: 'RUN', date: r.date_start || r.date_end, run: r });
    });

    const fallidas = (runs || []).filter(r => r.kind === 'CONCILIATION' && r.state !== 'COMPLETE');
    if (fallidas.length > 0) {
      items.push({ kind: 'FAILED_GROUP', date: fallidas[0].date_start, runs: fallidas });
    }

    return items.sort((a, b) => {
      const ta = a.date ? new Date(a.date).getTime() : 0;
      const tb = b.date ? new Date(b.date).getTime() : 0;
      return ta - tb;
    });
  }

  /** Duración legible a partir de milisegundos. Devuelve '—' si no se puede calcular. */
  formatDuration(ms: number | null): string {
    if (ms === null || ms === undefined || isNaN(ms) || ms < 0) return '—';
    const s = Math.round(ms / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ${s % 60}s`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ${m % 60}m`;
    return `${Math.floor(h / 24)}d ${h % 24}h`;
  }

  /** Distancia entre el primer y el último evento del lote. */
  totalDuration(): string {
    const s = this.timelineSummary;
    if (!s?.first_event || !s?.last_event) return '—';
    return this.formatDuration(new Date(s.last_event).getTime() - new Date(s.first_event).getTime());
  }

  /** Porcentaje de avance de una ejecución, acotado a 0-100. */
  runPercentage(run: any): number {
    const total = run?.progress?.total || 0;
    if (!total) return 0;
    return Math.min(100, Math.round((run.progress.processed / total) * 100));
  }

  /** Título de la ejecución: "Generación del archivo" o "Conciliación · intento N". */
  runTitle(run: any): string {
    if (run?.kind === 'GENERATION') return 'Generación del archivo';
    return run?.attempt ? `Conciliación · intento ${run.attempt}` : 'Conciliación';
  }

  /**
   * Autor de una ejecución. Devuelve null cuando no quedó registrado: el flujo no
   * guardaba el usuario hasta hace poco, así que los traces viejos no lo tienen y la
   * vista lo dice explícitamente en vez de mostrar un nombre inventado.
   */
  runAuthor(run: any): string | null {
    return run?.canceled_by || run?.executed_by || null;
  }

  closeTimelineModal() {
    this.showTimelineModal = false;
    this.selectedDirectDebit = null;
    this.timelineData = [];
    this.timelineCambios = [];
    this.timelineArchivos = [];
    this.timelineRuns = [];
    this.timelineSummary = null;
    this.timelineMerged = [];
    this.failedAttemptsCollapsed = true;
  }

  conciliationRunning = false;

  // Estado de descarga del Excel por lote (para el spinner del botón)
  downloadingExcel: { [lote: string]: boolean } = {};

  /** Descarga el Excel con los cobros del lote (cobros + clientes afectados). */
  async descargarCobrosExcel(dd: any) {
    const lote = dd?.lote || dd?.manual_lote;
    if (!lote) return;
    if (this.downloadingExcel[lote]) return;
    this.downloadingExcel[lote] = true;
    try {
      const blob = await this.directDebitService.downloadLoteCobrosExcel(lote);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cobros-lote-${lote}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      alertifyjs.error('No se pudo descargar el Excel del lote');
    } finally {
      this.downloadingExcel[lote] = false;
    }
  }

  async openPaymentsModal(directDebit: any) {
    this.selectedDirectDebit = directDebit;
    this.showPaymentsModal = true;
    this.paymentsData = [];
    this.paymentsPage = 1;
    this.paymentsSearch = '';
    this.paymentsStatusFilter = '';
    this.conciliationRunning = false;

    // Verificar si hay un proceso de conciliación activo para este lote
    if (directDebit.trace) {
      try {
        const traceResult = await this._user.traceDocumentsGet({
          type: 'DIRECTDEBIT_GENERATE_CONCILIATION_' + directDebit.trace
        });
        if (traceResult?.resp?.length > 0) {
          const traceCode = traceResult.resp[0].code || traceResult.resp[0]._id;
          const progress = await this._user.getConciliationProgress(traceCode);
          this.conciliationRunning = progress?.state === 'PENDING';
        }
      } catch (error) {
        // Si falla la verificación, asumir que no está corriendo
        this.conciliationRunning = false;
      }
    }

    this.activePaymentTab = this.conciliationRunning ? 'resumen' : 'detalle';
    await this.loadPayments();
  }

  closePaymentsModal() {
    // Los gráficos se destruyen a mano: Chart.js deja el canvas tomado y al reabrir el
    // modal fallaría al montar uno nuevo sobre el mismo elemento.
    this.donutChart?.destroy();
    this.barsChart?.destroy();
    this.donutChart = null;
    this.barsChart = null;
    this.stats = null;
    this.cerrarDetallePago();
    this.showPaymentsModal = false;
    this.selectedDirectDebit = null;
    this.paymentsData = [];
    this.paymentsCounts = { pending: 0, error: 0, completed: 0, total: 0 };
  }

  async loadPayments(loadMore = false) {
    if (loadMore) {
      this.loadingMorePayments = true;
    } else {
      this.loadingPayments = true;
    }

    try {
      const response = await this._user.getDirectDebitDetails(
        this.selectedDirectDebit._id,
        this.paymentsPage,
        this.paymentsLimit,
        this.paymentsSearch,
        this.paymentsStatusFilter
      );
      
      const data = response?.resp || {};
      
      if (loadMore) {
        this.paymentsData = [...this.paymentsData, ...(data.details || [])];
      } else {
        this.paymentsData = data.details || [];
        this.paymentsCounts = data.counts || { pending: 0, error: 0, completed: 0, total: 0 };
      }
      
      this.paymentsHasMore = data.pagination?.hasMore || false;
    } catch (error) {
      console.error('Error loading payments:', error);
      if (typeof alertifyjs !== 'undefined') {
        alertifyjs.error('Error al cargar cobros');
      }
    } finally {
      this.loadingPayments = false;
      this.loadingMorePayments = false;
    }
  }

  async onPaymentsScroll(event: any) {
    const element = event.target;
    // Check if we're near the bottom (within 50px)
    const atBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 50;
    
    if (atBottom && this.paymentsHasMore && !this.loadingMorePayments) {
      this.paymentsPage++;
      await this.loadPayments(true);
    }
  }

  async searchPayments() {
    this.paymentsPage = 1;
    this.paymentsData = [];
    await this.loadPayments();
  }

  /**
   * Filtra la tabla por el estado de la tarjeta clickeada.
   * Volver a clickear la tarjeta activa quita el filtro, igual que "Total".
   * Las tarjetas siguen mostrando los totales del lote, no los del filtro: si cambiaran
   * al filtrar, dejarían de servir para comparar.
   */
  async filtrarPorEstado(estado: '' | 'pending' | 'error' | 'completed') {
    this.paymentsStatusFilter = (this.paymentsStatusFilter === estado && estado !== '') ? '' : estado;
    this.paymentsPage = 1;
    this.paymentsData = [];
    await this.loadPayments();
  }

  // ── Detalle del pago real de una fila ──────────────────────────────────────
  // No se arma un detalle propio: se resuelve el pago y se abre el componente genérico
  // (app-person-payments), el mismo que usa la consola de pagos, para no duplicar ni
  // divergir de la estructura que ya existe.
  expandedPaymentRef: string | null = null;
  loadingPaymentDetail = false;
  showPaymentDetailModal = false;
  paymentDetailCode: string | null = null;
  paymentDetailPersonCode: string | null = null;
  paymentDetailPerson: string | null = null;

  /**
   * Resuelve el pago real detrás de la fila y abre el detalle genérico.
   * El vínculo es (id_direct_debit + referencia): el banco recicla las referencias entre
   * lotes, así que la referencia sola no identifica un pago.
   */
  async verDetallePago(row: any) {
    const ref = String(row?.reference || '');
    if (!ref) return;

    this.expandedPaymentRef = ref;
    this.loadingPaymentDetail = true;
    try {
      const resp = await this._user.getDirectDebitDetailPayment(this.selectedDirectDebit._id, ref);
      const d = resp?.resp;

      if (!d?.found || !d?.payment?.code) {
        alertifyjs.warning('No se encontró un pago registrado para esta referencia en este lote.');
        return;
      }
      if (d.duplicates > 0) {
        alertifyjs.warning(`Hay ${d.duplicates + 1} pagos con esta referencia en el lote. Se abre el primero.`);
      }

      this.abrirModalPago(d.payment.code, d.payment.person_code, d.payment.person_name);
    } catch (error) {
      console.error('Error cargando el detalle del pago:', error);
      alertifyjs.error('Error al abrir el detalle del pago');
    } finally {
      this.loadingPaymentDetail = false;
      this.expandedPaymentRef = null;
    }
  }

  // ── Estadísticas del lote ──────────────────────────────────────────────────
  @ViewChild('statsDonut') statsDonutRef?: ElementRef<HTMLCanvasElement>;
  @ViewChild('statsBars') statsBarsRef?: ElementRef<HTMLCanvasElement>;
  stats: any = null;
  loadingStats = false;
  private donutChart: Chart | null = null;
  private barsChart: Chart | null = null;

  /** Sólo los lotes cerrados tienen resultado que analizar. */
  loteCerrado(): boolean {
    return ['FINISHED', 'PROCESING'].indexOf(this.selectedDirectDebit?.status) !== -1;
  }

  async cargarEstadisticas() {
    if (!this.selectedDirectDebit?._id) return;
    this.loadingStats = true;
    this.stats = null;
    try {
      const resp = await this._user.getDirectDebitStats(this.selectedDirectDebit._id);
      this.stats = resp?.resp || null;
      // Los canvas recién existen cuando Angular pinta la pestaña.
      setTimeout(() => this.dibujarGraficos(), 50);
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
      alertifyjs.error('Error al cargar las estadísticas del lote');
    } finally {
      this.loadingStats = false;
    }
  }

  /**
   * Convierte a bolívares con la TASA DEL LOTE, que es la que se usó al generarlo.
   * No sirve la tasa del día: el lote ya se cobró con la suya y usar otra daría un
   * número que no coincide con lo que el banco movió.
   */
  enBs(monto: number): number | null {
    const tasa = this.stats?.lote?.rate || this.selectedDirectDebit?.rate || 0;
    if (!tasa) return null;
    return (Number(monto) || 0) * tasa;
  }

  /** Porcentaje sobre el monto total enviado, no sobre la cantidad de registros. */
  porcentajeMonto(monto: number): string {
    const total = this.stats?.montos?.total?.monto || 0;
    if (!total) return '0';
    return ((monto / total) * 100).toFixed(1);
  }

  private dibujarGraficos() {
    const m = this.stats?.montos;
    if (!m) return;

    // Se destruyen los anteriores: Chart.js no reemplaza el gráfico de un canvas ya usado.
    this.donutChart?.destroy();
    this.barsChart?.destroy();

    const donut = this.statsDonutRef?.nativeElement;
    if (donut) {
      this.donutChart = new Chart(donut, {
        type: 'doughnut',
        data: {
          labels: ['Cobrado', 'Rechazado', 'Sin procesar'],
          datasets: [{
            data: [m.completed.monto, m.error.monto, m.pending.monto],
            backgroundColor: ['#16a34a', '#dc2626', '#f59e0b'],
            borderWidth: 0
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11 } } },
            tooltip: {
              callbacks: {
                label: (ctx: any) => {
                  const v = ctx.parsed || 0;
                  return ` ${ctx.label}: ${v.toLocaleString('es-VE', { minimumFractionDigits: 2 })} (${this.porcentajeMonto(v)}%)`;
                }
              }
            }
          }
        }
      });
    }

    const bars = this.statsBarsRef?.nativeElement;
    const rechazos = (this.stats?.rechazos || []).slice(0, 6);
    if (bars && rechazos.length > 0) {
      this.barsChart = new Chart(bars, {
        type: 'bar',
        data: {
          labels: rechazos.map((r: any) => (r.descripcion || r.codigo || '—').slice(0, 34)),
          datasets: [{ data: rechazos.map((r: any) => r.registros), backgroundColor: '#dc2626', borderWidth: 0 }]
        },
        options: {
          indexAxis: 'y',
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            x: { ticks: { font: { size: 10 } } },
            y: { ticks: { font: { size: 10 } } }
          }
        }
      });
    }
  }

  /**
   * Abre el detalle genérico con los datos ya resueltos. Lo usan el botón de la tabla
   * (que primero busca el pago por referencia) y la lista de pagos con saldo sin aplicar
   * de las estadísticas, que ya los trae resueltos.
   */
  abrirModalPago(code: string, personCode?: string | null, personName?: string | null) {
    if (!code) return;
    this.paymentDetailCode = code;
    this.paymentDetailPersonCode = personCode || null;
    this.paymentDetailPerson = personName || null;
    this.showPaymentDetailModal = true;
  }

  cerrarDetallePago() {
    this.showPaymentDetailModal = false;
    this.paymentDetailCode = null;
    this.paymentDetailPersonCode = null;
    this.paymentDetailPerson = null;
  }

  /** Cuántos registros corresponden al filtro activo, para el encabezado de la tabla. */
  get totalDelFiltro(): number {
    const c = this.paymentsCounts;
    if (this.paymentsStatusFilter === 'pending') return c.pending;
    if (this.paymentsStatusFilter === 'error') return c.error;
    if (this.paymentsStatusFilter === 'completed') return c.completed;
    return c.total;
  }

  /** Etiqueta legible del filtro activo. */
  get etiquetaFiltro(): string {
    if (this.paymentsStatusFilter === 'pending') return 'Pendientes';
    if (this.paymentsStatusFilter === 'error') return 'No cobrados';
    if (this.paymentsStatusFilter === 'completed') return 'Completados';
    return 'Todos';
  }

  clearSearch() {
    this.paymentsSearch = '';
    this.searchPayments();
  }

  /**
   * Verifica si la domiciliación es de seguros
   */
  isInsuranceDomiciliation(directDebit: any): boolean {
    return directDebit?.params?.product_type === 'SEGUROS_MERCANTIL' ||
           directDebit?.product?.code === 'SEGUROS_MERCANTIL';
  }

  /**
   * Abre el modal de reporte de seguros
   */
  async openInsuranceReportModal(directDebit: any) {
    console.log('Opening insurance report modal for:', directDebit);
    this.selectedDirectDebit = directDebit;
    this.showInsuranceReportModal = true;
    this.loadingInsuranceReport = true;
    this.insuranceReportData = [];
    this.insuranceReportStats = null;

    try {
      const response = await this._user.getInsuranceReportTracking(directDebit._id, { limit: 100 });
      const data = response?.resp || response || {};
      this.insuranceReportData = data.data || [];
      this.insuranceReportStats = data.stats || null;
    } catch (error) {
      console.error('Error loading insurance report:', error);
      if (typeof alertifyjs !== 'undefined') {
        alertifyjs.error('Error al cargar el reporte de seguros');
      }
    } finally {
      this.loadingInsuranceReport = false;
    }
  }

  /**
   * Cierra el modal de reporte de seguros
   */
  closeInsuranceReportModal() {
    this.showInsuranceReportModal = false;
    this.selectedDirectDebit = null;
    this.insuranceReportData = [];
    this.insuranceReportStats = null;
  }

  /**
   * Exporta el reporte de seguros a CSV
   */
  exportInsuranceReport() {
    if (!this.selectedDirectDebit) return;
    const url = this._user.getInsuranceReportExportUrl(this.selectedDirectDebit._id);
    window.open(url, '_blank');
  }

  /**
   * Obtiene clase CSS según estado del reporte
   */
  getReportStatusClass(item: any): string {
    if (item.reportado) {
      return 'bg-green-100 text-green-800';
    } else if (item.permanently_failed || item.current_attempt >= item.max_attempts) {
      return 'bg-red-100 text-red-800';
    } else if (item.current_attempt > 0) {
      return 'bg-yellow-100 text-yellow-800';
    } else {
      return 'bg-gray-100 text-gray-800';
    }
  }

  /**
   * Obtiene texto del estado del reporte
   */
  getReportStatusText(item: any): string {
    if (item.reportado) {
      return '✓ Reportado';
    } else if (item.permanently_failed || item.current_attempt >= item.max_attempts) {
      return '✗ Límite de intentos vencido';
    } else if (item.current_attempt > 0) {
      return `⟳ Reintentando (${item.current_attempt}/${item.max_attempts})`;
    } else {
      return '⏳ Pendiente';
    }
  }

  /**
   * Formatea fecha para mostrar
   */
  formatDateTime(date: string | null): string {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('es-VE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  switchPaymentTab(tab: 'resumen' | 'detalle' | 'estadisticas') {
    if (tab === 'resumen' && this.activePaymentTab !== 'resumen') {
      // Al cambiar a resumen, forzar recreación del componente
      const currentDirectDebit = this.selectedDirectDebit;
      this.selectedDirectDebit = null;
      this.activePaymentTab = tab;
      
      setTimeout(() => {
        this.selectedDirectDebit = currentDirectDebit;
      }, 10);
    } else {
      this.activePaymentTab = tab;
      // Las estadísticas se piden al entrar, no al abrir el modal: son una agregación
      // que puede tardar unos segundos en lotes grandes y no siempre se miran.
      if (tab === 'estadisticas' && !this.stats) {
        this.cargarEstadisticas();
      }
    }
  }

  async refreshPayments() {
    this.paymentsPage = 1;
    this.paymentsData = [];

    // Cargar pagos primero (antes de nulificar selectedDirectDebit)
    await this.loadPayments();

    // Forzar recreación del componente de progreso ocultándolo temporalmente
    const currentDirectDebit = this.selectedDirectDebit;
    this.selectedDirectDebit = null;

    // Restaurar después de un pequeño delay para que Angular detecte el cambio
    setTimeout(() => {
      this.selectedDirectDebit = currentDirectDebit;
    }, 10);
  }

  /**
   * Título corto del evento. getStatusLabel() devuelve oraciones largas que servían a la
   * vez de título y de etiqueta —el mismo texto repetido dos veces en cada tarjeta, e
   * ilegible dentro de una píldora—. Acá va el título; la oración queda de descripción.
   */
  getStatusShort(status: string): string {
    const map: any = {
      'INITIAL': 'Proceso iniciado',
      'GENERATED': 'Archivo generado',
      'DOWNLOADED': 'Archivo descargado',
      'UPLOADED': 'Cargado al banco',
      'PROCESING': 'Respuesta del banco procesada',
      'FINISHED': 'Proceso finalizado',
      'ABORTED': 'Proceso abortado'
    };
    return map[status] || status;
  }

  getStatusLabel(status: string): string {
    const statusMap: any = {
      'INITIAL': 'Sin comenzar',
      'GENERATED': 'El archivo de cobranza fue generado',
      'DOWNLOADED': 'El archivo de cobranza ha sido descargado',
      'UPLOADED': 'El archivo ya fue cargado en el banco y se encuentra en espera de respuesta',
      'PROCESING': 'Se recibió la respuesta del banco y se encuentra procesando',
      'FINISHED': 'El proceso se finalizó y ya se realizó la conciliación',
      'ABORTED': 'Proceso abortado'
    };
    return statusMap[status] || status;
  }

  async downloadFile(url: string, extension: string, name: string) {
    try {
      // La extensión que llega por parámetro es un respaldo: la real se deduce de la URL.
      // Si no se hace, la respuesta de bancos que la envían en Excel (Bancaribe) se baja
      // como .txt y se abre como binario ilegible.
      const ext = this.directDebitService.extensionRealDeUrl(url) || extension;
      const res = await this.directDebitService.downloadImage(url, ext);
      if (res) {
        saveAs(res, `${name}.${ext}`);
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      if (typeof alertifyjs !== 'undefined') {
        alertifyjs.error('Error al descargar el archivo');
      }
    }
  }

  /**
   * Descarga un archivo de respuesta del banco respaldado.
   *
   * Usa downloadFile y no un href directo a Cloudinary porque la extensión real se
   * deduce de la URL: si no, la respuesta de los bancos que la mandan en Excel
   * (Bancaribe) se baja como .txt y se abre como binario ilegible.
   *
   * El nombre conserva el original, que es como el operador lo reconoce.
   */
  descargarArchivoRespuesta(archivo: any) {
    if (!archivo?.url) return;
    // Se quita la extensión del nombre: downloadFile la vuelve a añadir con la real.
    const base = String(archivo.file_name || `respuesta_banco_intento_${archivo.attempt}`)
      .replace(/\.[^.]+$/, '');
    const extensionDeclarada = String(archivo.file_name || '').split('.').pop() || 'txt';
    this.downloadFile(archivo.url, extensionDeclarada, base);
  }

  getStatusIcon(status: string): string {
    const iconMap: any = {
      'INITIAL': 'fa-play-circle',
      'GENERATED': 'fa-file-alt',
      'DOWNLOADED': 'fa-download',
      'UPLOADED': 'fa-upload',
      'PROCESING': 'fa-spinner',
      'FINISHED': 'fa-check-circle',
      'ABORTED': 'fa-times-circle'
    };
    return iconMap[status] || 'fa-circle';
  }

  getStatusColor(status: string): string {
    const colorMap: any = {
      'INITIAL': 'text-blue-600 bg-blue-100',
      'GENERATED': 'text-indigo-600 bg-indigo-100',
      'DOWNLOADED': 'text-purple-600 bg-purple-100',
      'UPLOADED': 'text-cyan-600 bg-cyan-100',
      'PROCESING': 'text-yellow-600 bg-yellow-100',
      'FINISHED': 'text-green-600 bg-green-100',
      'ABORTED': 'text-red-600 bg-red-100'
    };
    return colorMap[status] || 'text-gray-600 bg-gray-100';
  }

  goToProductsManagement() {
    this.router.navigate(['/console/type_product']);
  }

  goBack() {
    this.router.navigate(['/console/dashboard-direct-debit']);
  }

  // Bulk actions
  openBulkEditModal() {
    if (this.selectedProducts.size === 0) {
      alertifyjs.warning('Selecciona al menos un producto');
      return;
    }
    this.showBulkEditModal = true;
    this.bulkBanksToAdd = [];
    this.bulkTypesToAdd = [];
    this.bulkAction = 'add';
  }

  closeBulkEditModal() {
    this.showBulkEditModal = false;
  }

  async applyBulkEdit() {
    if (this.bulkBanksToAdd.length === 0 && this.bulkTypesToAdd.length === 0) {
      alertifyjs.warning('Selecciona al menos un banco o tipo');
      return;
    }

    this.isLoading = true;
    try {
      const selectedCodes = Array.from(this.selectedProducts);
      
      for (const code of selectedCodes) {
        const product = this.products.find(p => p.code === code);
        if (!product) continue;

        let newBanks = [...(product.domiciliacion.allowed_banks || [])];
        let newTypes = [...(product.domiciliacion.allowed_types || [])];

        if (this.bulkAction === 'add') {
          // Add banks/types
          this.bulkBanksToAdd.forEach(b => {
            if (!newBanks.includes(b)) newBanks.push(b);
          });
          this.bulkTypesToAdd.forEach(t => {
            if (!newTypes.includes(t)) newTypes.push(t);
          });
        } else if (this.bulkAction === 'replace') {
          // Replace banks/types
          if (this.bulkBanksToAdd.length > 0) newBanks = [...this.bulkBanksToAdd];
          if (this.bulkTypesToAdd.length > 0) newTypes = [...this.bulkTypesToAdd];
        } else if (this.bulkAction === 'remove') {
          // Remove banks/types
          newBanks = newBanks.filter(b => !this.bulkBanksToAdd.includes(b));
          newTypes = newTypes.filter(t => !this.bulkTypesToAdd.includes(t));
        }

        // Save
        await this._user.setRemanenteNeteo({
          product_code: code,
          domiciliacion: {
            status: product.domiciliacion.status,
            allowed_banks: newBanks,
            allowed_types: newTypes
          }
        });
      }

      alertifyjs.success(`Configuración actualizada para ${selectedCodes.length} producto(s)`);
      this.closeBulkEditModal();
      this.clearSelection();
      await this.loadProductsData();
    } catch (error) {
      console.error('Error applying bulk edit:', error);
      alertifyjs.error('Error al aplicar cambios masivos');
    } finally {
      this.isLoading = false;
    }
  }

  // Export
  async loadDeactivationBatches(page: number = 1) {
    this.isLoadingDeactivationBatches = true;
    this.deactivationBatchesPage = page;
    try {
      const resp = await this.directDebitService.getDeactivationBatches(page, this.deactivationBatchesLimit);
      if (resp?.success) {
        this.deactivationBatches = resp.batches || [];
        this.deactivationBatchesTotal = resp.total || 0;
      }
    } catch (error) {
      console.error('Error cargando batches de baja:', error);
      alertifyjs.error('Error al cargar procesos de baja por morosidad');
    } finally {
      this.isLoadingDeactivationBatches = false;
    }
  }

  async openDeactivationBatchDetail(batch: any) {
    this.selectedDeactivationBatch = batch;
    this.showDeactivationDetailModal = true;
    this.isLoadingDeactivationDetail = true;
    this.selectedDeactivationBatchDetail = null;
    this.affectedPersonsData = [];
    this.affectedPersonsTotal = 0;
    this.affectedPersonsPage = 1;
    this.affectedPersonsSearch = '';
    try {
      const resp = await this.directDebitService.getDeactivationBatchDetail(batch._id);
      if (resp?.success) {
        this.selectedDeactivationBatchDetail = resp.batch;
        await this.loadAffectedPersons(1);
      }
    } catch (error) {
      console.error('Error cargando detalle de batch:', error);
      alertifyjs.error('Error al cargar el detalle del lote');
    } finally {
      this.isLoadingDeactivationDetail = false;
    }
  }

  async loadAffectedPersons(page = 1, append = false) {
    if (!this.selectedDeactivationBatch?._id) return;
    this.isLoadingMorePersons = true;
    try {
      const resp = await this.directDebitService.getAffectedPersonsPaginated(
        this.selectedDeactivationBatch._id,
        page,
        this.affectedPersonsLimit,
        this.affectedPersonsSearch
      );
      if (resp?.success) {
        this.affectedPersonsData = append ? [...this.affectedPersonsData, ...resp.data] : resp.data;
        this.affectedPersonsTotal = resp.total;
        this.affectedPersonsPage = page;
      }
    } catch (error) {
      console.error('Error cargando personas afectadas:', error);
      alertifyjs.error('Error al cargar las personas afectadas');
    } finally {
      this.isLoadingMorePersons = false;
    }
  }

  async loadMoreAffectedPersons() {
    await this.loadAffectedPersons(this.affectedPersonsPage + 1, true);
  }

  async searchAffectedPersonsInBatch() {
    this.affectedPersonsPage = 1;
    this.affectedPersonsData = [];
    this.isSearchingPersons = true;
    await this.loadAffectedPersons(1, false);
    this.isSearchingPersons = false;
  }

  clearAffectedPersonsSearch() {
    this.affectedPersonsSearch = '';
    this.affectedPersonsPage = 1;
    this.affectedPersonsData = [];
    this.loadAffectedPersons(1, false);
  }

  get hasMoreAffectedPersons(): boolean {
    return this.affectedPersonsData.length < this.affectedPersonsTotal;
  }

  closeDeactivationDetailModal() {
    this.showDeactivationDetailModal = false;
    this.selectedDeactivationBatch = null;
    this.selectedDeactivationBatchDetail = null;
    this.affectedPersonsData = [];
    this.affectedPersonsTotal = 0;
    this.affectedPersonsSearch = '';
  }

  exportDeactivationCsv(batchId: string) {
    if (!batchId) return;
    this.directDebitService.exportDeactivationBatchCsv(batchId);
  }

  async searchDeactivationByIdentity() {
    const identity = this.deactivationSearchIdentity?.trim();
    if (!identity) {
      alertifyjs.warning('Ingrese una cédula para buscar');
      return;
    }
    this.isSearchingDeactivation = true;
    this.deactivationSearchResult = null;
    try {
      const resp = await this.directDebitService.searchDeactivationByIdentity(identity);
      if (resp?.success) {
        this.deactivationSearchResult = resp;
      }
    } catch (error) {
      console.error('Error en búsqueda:', error);
      alertifyjs.error('Error al buscar el historial');
    } finally {
      this.isSearchingDeactivation = false;
    }
  }

  clearDeactivationSearch() {
    this.deactivationSearchIdentity = '';
    this.deactivationSearchResult = null;
  }

  async runDeactivationManual() {
    if (this.isRunningDeactivation) return;
    this.isRunningDeactivation = true;
    try {
      const resp = await this.directDebitService.runDeactivationManual({ product_types: ['CREDIT'] });
      if (resp?.success) {
        alertifyjs.success('Proceso de baja iniciado en background. Recargue en unos minutos.');
      }
    } catch (error) {
      console.error('Error ejecutando proceso manual:', error);
      alertifyjs.error('Error al iniciar el proceso manual');
    } finally {
      this.isRunningDeactivation = false;
    }
  }

  exportToCSV() {
    const headers = ['Código', 'Nombre', 'Estado', 'Bancos', 'Tipos'];
    const rows = this.filteredProducts.map(p => [
      p.code,
      p.name,
      p.domiciliacion.status ? 'Activo' : 'Inactivo',
      (p.domiciliacion.allowed_banks || []).join(';'),
      (p.domiciliacion.allowed_types || []).join(';')
    ]);
    
    const csv = [
      headers.join(','),
      ...rows.map(r => r.map(c => `"${c}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `domiciliacion_config_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  }
}
